/**
 * Últimos contatos REALIZADOS com o cliente, a partir da timeline do CRM.
 *
 * ⚠️ As regras abaixo são ESPELHO das que vivem no `profile-contatos.vue` da
 * página `/cliente-perfil`. Não foram extraídas de lá: aquele componente é de
 * outra frente e não se mexe por causa desta. A consequência é conhecida e
 * aceita — mudou a regra lá, mude aqui.
 *
 * Espelhar exigiu cuidado porque cada regra veio de um defeito real, e o teste
 * ao lado guarda os três: tarefa com vencimento no FUTURO virando "último
 * contato", tarefa CANCELADA contando como contato, e atividade sem hora
 * exibida como reunião à meia-noite.
 */
import { aawzDate } from '@utils/dates/aawz-date'

// O array `historico` mistura DUAS fontes: as atividades do CRM (`atividade`,
// que o backend só grava depois de acontecer) e as tarefas manuais do
// calendário, cujo `data_atividade` é o VENCIMENTO e pode estar no futuro. Só a
// primeira é contato garantido.
const TIPO_EVENTO_ATIVIDADE = 'atividade'
// O backend marca a tarefa manual em DOIS campos. Checar os dois evita que a
// classificação dependa de um só.
const TIPO_EVENTO_TAREFA_MANUAL = 'tarefa_manual'
const TIPO_ATIVIDADE_MANUAL = 'manual'

// ⚠️ Concluída e cancelada colapsam no mesmo `status_evento: 'executada'`. Quem
// desempata é o `resultado`, que preserva o status cru.
const STATUS_CANCELADO = new Set([
  'cancelado',
  'cancelada',
  'canceled',
  'cancelled',
])
const STATUS_REALIZADO = new Set([
  'concluido',
  'concluida',
  'concluído',
  'finalizado',
  'finalizada',
  'done',
  'executado',
  'executada',
])

const FORMATO_COM_HORA = 'DD/MM/YYYY HH:mm'
const FORMATO_SEM_HORA = 'DD/MM/YYYY'
// Sufixo que o backend imprime para atividade registrada sem horário.
const MEIA_NOITE = ' 00:00'
const ZERO = 0

const minusculo = (valor) => String(valor || '').toLowerCase()

const ehCancelado = (item) => STATUS_CANCELADO.has(minusculo(item.resultado))

const ehTarefaManual = (item) =>
  item.tipo_evento === TIPO_EVENTO_TAREFA_MANUAL ||
  minusculo(item.tipo_atividade) === TIPO_ATIVIDADE_MANUAL

const temStatusRealizado = (item) =>
  STATUS_REALIZADO.has(minusculo(item.status_evento))

/**
 * ★ Tarefa manual é COMPROMISSO até ser concluída — inclusive com prazo
 * vencido. Sem esta ordem de avaliação, uma tarefa marcada para a semana que vem
 * aparecia como o último contato do cliente.
 */
const ehContatoRealizado = (item) => {
  if (!item) return false
  if (ehCancelado(item)) return false
  if (ehTarefaManual(item)) return temStatusRealizado(item)
  if (item.tipo_evento === TIPO_EVENTO_ATIVIDADE) return true
  return temStatusRealizado(item)
}

// Para a tarefa concluída vale a data de EXECUÇÃO, não a de vencimento.
const dataDoContato = (item) =>
  item._data_execucao_override || item.data_atividade

const paraTimestamp = (texto) => {
  const valor = String(texto || '')
  if (!valor) return ZERO
  const formato = valor.includes(':') ? FORMATO_COM_HORA : FORMATO_SEM_HORA
  const data = aawzDate(valor, formato)
  const nativa = data && data.toDate ? data.toDate() : null
  const ts = nativa ? nativa.getTime() : NaN
  return isFinite(ts) ? ts : ZERO
}

/**
 * ★ Atividade registrada só com data chega com "00:00" porque o backend imprime
 * %H:%M de qualquer jeito. Mostrar isso inventa uma reunião à meia-noite.
 */
const dataExibida = (item) => {
  const data = String(dataDoContato(item) || '')
  return data.endsWith(MEIA_NOITE) ? data.split(' ')[0] : data
}

/**
 * @param {Object} timeline `value` da resposta de `apiCrmTimelineClient`
 * @param {Object=} opcoes `{ maximo }`
 * @returns {{contatos: Array<{data, canal, resumo}>, temMais: boolean}}
 */
export const ultimosContatos = (timeline, opcoes) => {
  const maximo = (opcoes || {}).maximo
  const historico = (timeline && timeline.historico) || []
  // Fim do dia, e não "agora": um contato registrado hoje mais tarde continua
  // sendo de hoje, e cortá-lo pelo relógio esconderia o mais recente.
  const fimDoDia = aawzDate().endOf('day').toDate().getTime()

  const ordenados = historico
    .filter(ehContatoRealizado)
    .map((item) => ({ item, ts: paraTimestamp(dataDoContato(item)) }))
    .filter(({ ts }) => ts > ZERO && ts <= fimDoDia)
    .sort((a, b) => b.ts - a.ts)

  const teto = maximo === undefined ? ordenados.length : maximo

  return {
    contatos: ordenados.slice(0, teto).map(({ item }) => ({
      data: dataExibida(item),
      canal: item.tipo_atividade || '',
      resumo: item.titulo || item.descricao || '',
    })),
    temMais: ordenados.length > teto,
  }
}
